import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import Markdown from 'markdown-to-jsx';
import { Alert, Button, Select, Skeleton, Spin, Tag } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CopyOutlined,
  DownOutlined,
  DownloadOutlined,
  ReloadOutlined,
  RightOutlined,
  SendOutlined
} from '@ant-design/icons';
import { PlaygroundContext } from './context/PlaygroundContext';
import { BASE_PATH } from './routes';
import {
  GET_AGENTS,
  GET_ALIAS_DETAILS,
  POLL_AGENT_JOB_RESULT,
  SEND_AGENT_MESSAGE
} from './graphql/mutations';
import { PARTNER_OPTIONS } from './constants';
import { ResourceItem } from './components/ResourceSelector';
import { useAgentJobPoller } from './hooks/useAgentJobPoller';

const { Option } = Select;

const VALID_STATUSES = ['PREPARED', 'ACTIVE', 'READY'];
const AGENTS_CACHE_KEY = 'llm-playground-agents-cache';
const LAST_AGENT_KEY = 'llm-playground-last-agent';

type AgentMessage = {
  role: 'user' | 'agent';
  content: string;
};

const AgentInteractionV2Inner: React.FC = () => {
  const { pushError } = useContext(PlaygroundContext);
  const history = useHistory();
  const location = useLocation();

  const { data: agentsData, loading: agentsFetching, error: agentsFetchError } = useQuery(GET_AGENTS, {
    fetchPolicy: 'network-only'
  });
  const [sendAgentMessage] = useMutation(SEND_AGENT_MESSAGE);
  const [pollAgentJobResult] = useMutation(POLL_AGENT_JOB_RESULT);
  const [getAliasDetailsMutation] = useMutation(GET_ALIAS_DETAILS);

  const preselectedAgentName = (() => {
    const fromParam = new URLSearchParams(location.search).get('name');
    if (fromParam) return fromParam;
    return localStorage.getItem(LAST_AGENT_KEY) ?? '';
  })();

  const [agents, setAgents] = useState<ResourceItem[]>(() => {
    try {
      const stored = localStorage.getItem(AGENTS_CACHE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.warn('[AgentInteractionV2] Corrupted agent cache, clearing:', err);
      try { localStorage.removeItem(AGENTS_CACHE_KEY); } catch { /* ignore */ }
      return [];
    }
  });

  const agentsLoading = agentsFetching && agents.length === 0;

  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedAliasId, setSelectedAliasId] = useState('');
  const [aliasDetails, setAliasDetails] = useState<any>(null);
  const [loadingAliasDetails, setLoadingAliasDetails] = useState(false);
  const [partner, setPartner] = useState('melio');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const { pollJob } = useAgentJobPoller(pollAgentJobResult, isMountedRef);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!agentsFetchError) return;
    console.error('[AgentInteractionV2] Failed to fetch agents:', agentsFetchError);
    if (agents.length === 0) {
      pushError({ title: 'Could not load agents', description: 'Failed to fetch available agents. The list may be outdated or empty.' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentsFetchError]);

  useEffect(() => {
    const fresh: ResourceItem[] | undefined = agentsData?.agentsList;
    if (!fresh) return;
    setAgents((prev) => {
      const next = JSON.stringify(fresh);
      if (next === JSON.stringify(prev)) return prev;
      try { localStorage.setItem(AGENTS_CACHE_KEY, next); } catch { /* storage full */ }
      return fresh;
    });
    if (selectedAgentId && !messages.length && !fresh.some((a) => a.id === selectedAgentId)) {
      setSelectedAgentId('');
      setSelectedAliasId('');
      setAliasDetails(null);
      try { localStorage.removeItem(LAST_AGENT_KEY); } catch { /* ignore */ }
    }
  }, [agentsData, selectedAgentId, messages.length]);

  const filteredAgents = agents.filter((a) => VALID_STATUSES.includes(a.status?.toUpperCase() ?? ''));
  const selectedAgent = agents.find((a) => a.id === selectedAgentId) ?? null;

  useEffect(() => {
    if (!agents.length || selectedAgentId) return;
    const partnerParam = new URLSearchParams(location.search).get('partner');
    if (partnerParam && PARTNER_OPTIONS.some((p) => p.value === partnerParam)) setPartner(partnerParam);
    if (preselectedAgentName) {
      const match = agents.find((a) => a.name === preselectedAgentName);
      if (match) handleAgentSelect(match.id, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, preselectedAgentName]);

  const fetchAliasDetails = async (agentName: string, aliasName: string) => {
    setLoadingAliasDetails(true);
    try {
      const { data } = await getAliasDetailsMutation({
        variables: { agentName, aliasName }
      });
      setAliasDetails(data?.getAliasDetails);
    } catch (err) {
      console.error('[AgentInteractionV2] Failed to fetch alias details:', err);
    } finally {
      setLoadingAliasDetails(false);
    }
  };

  const handleAgentSelect = async (agentId: string, skipIfSame = false) => {
    if (skipIfSame && agentId === selectedAgentId) return;

    setSelectedAgentId(agentId);
    setAliasDetails(null);
    setSelectedAliasId('');
    setMessages([]);
    setError(null);

    const agent = agents.find((a) => a.id === agentId);
    if (agent?.name) {
      try { localStorage.setItem(LAST_AGENT_KEY, agent.name); } catch { /* storage full */ }
    }
    if (!agent?.aliases?.length) return;

    const bestAlias = agent.aliases.find((a: any) => a.name === 'Live') ?? agent.aliases[0];
    setSelectedAliasId(bestAlias.name);
    await fetchAliasDetails(agent.name, bestAlias.name);
  };

  const handleAliasSelect = async (aliasId: string) => {
    setSelectedAliasId(aliasId);
    setAliasDetails(null);
    if (!selectedAgent) return;
    const alias = selectedAgent.aliases?.find((a: any) => a.name === aliasId);
    if (!alias) return;
    await fetchAliasDetails(selectedAgent.name, alias.name);
  };

  const doSend = async (text: string) => {
    if (!text.trim() || isSending) return;

    const agentName = selectedAgent?.name ?? selectedAgentId;
    if (!agentName) {
      pushError({ title: 'No agent selected', description: 'Please select an agent first.' });
      return;
    }

    const aliasName = selectedAliasId || undefined;
    const aliasVersion = aliasDetails?.agentVersion;

    const userMsg: AgentMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setError(null);
    setIsSending(true);

    try {
      const conversationId = 'stateless-' + Date.now();
      const messageHistory = updatedMessages
        .filter((m) => m.role === 'user')
        .map((m) => ({ role: m.role, content: m.content }));

      const { data: submitData } = await sendAgentMessage({
        variables: {
          conversationId,
          message: text,
          messages: messageHistory,
          agentName,
          partner,
          aliasName,
          aliasVersion
        }
      });

      if (!submitData?.sendAgentMessage?.jobId) {
        throw new Error('Failed to submit agent job');
      }

      const { jobId } = submitData.sendAgentMessage;

      const result = await pollJob(jobId, partner);
      setMessages((prev) => [...prev, { role: 'agent', content: result.content ?? '' }]);
    } catch (err: any) {
      const graphqlMsg = err?.graphQLErrors?.[0]?.message;
      const networkMsg = err?.networkError?.result?.errors?.[0]?.message ?? err?.networkError?.message;
      setError(graphqlMsg ?? networkMsg ?? err?.message ?? String(err));
      console.error('[AgentRunner] send failed:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = () => doSend(input);

  const resetSession = () => {
    setIsResetting(true);
    setTimeout(() => {
      setMessages([]);
      setInput('');
      setError(null);
      setCopied(false);
      setIsResetting(false);
    }, 200);
  };

  const hasSentOrResponded = messages.length > 0 || isSending;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleSend();
  };

  const lastAgentMessage = messages.findLast((m) => m.role === 'agent');

  const copyResponse = async () => {
    if (!lastAgentMessage) return;
    try {
      await navigator.clipboard.writeText(lastAgentMessage.content);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[AgentInteractionV2] Clipboard write failed:', err);
      pushError({ title: 'Copy failed', description: 'Could not write to clipboard. Please select and copy the text manually.' });
    }
  };

  const downloadResponse = () => {
    if (!lastAgentMessage) return;
    try {
      const blob = new Blob([lastAgentMessage.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const agentSlug = selectedAgent?.name?.toLowerCase().replace(/\s+/g, '-') ?? 'agent';
      const date = new Date().toISOString().slice(0, 10);
      a.download = `${agentSlug}-${date}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[AgentInteractionV2] Download failed:', err);
      pushError({ title: 'Download failed', description: 'Could not download the response. Try copying it manually instead.' });
    }
  };

  const isSendDisabled = !selectedAgentId || !input.trim() || isSending || hasSentOrResponded;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: '24px 20px' }}>
      <div style={{ maxWidth: 740, margin: '0 auto' }}>

        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push(`${BASE_PATH}/main`)}
          style={{ padding: 0, marginBottom: 20, fontSize: 13 }}
        >
          Back to menu
        </Button>

        <div style={{ marginBottom: 28 }}>
          <h2 style={{ margin: 0, marginBottom: 4, fontSize: 22, fontWeight: 600, color: '#141414' }}>
            AI Agent Runner
          </h2>
          <p style={{ margin: 0, color: '#8c8c8c', fontSize: 14 }}>
            Select an agent, compose a message, and view the response.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Agent
          </label>
          <Select
            showSearch
            value={selectedAgentId || (agentsLoading && preselectedAgentName ? '__loading__' : undefined)}
            onChange={handleAgentSelect}
            loading={agentsLoading}
            disabled={hasSentOrResponded}
            placeholder="Select an agent..."
            style={{ width: '100%' }}
            notFoundContent={
              agentsLoading ? (
                <div style={{ padding: '8px 0' }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{ padding: '6px 12px' }}>
                      <Skeleton.Input active size="small" style={{ width: `${60 + i * 10}%`, height: 16 }} />
                    </div>
                  ))}
                </div>
              ) : 'No agents found'
            }
            filterOption={(input, option) =>
              String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {agentsLoading && preselectedAgentName && (
              <Option key="__loading__" value="__loading__" disabled>
                <span style={{ color: '#595959' }}>{preselectedAgentName}</span>
                <Spin size="small" style={{ marginLeft: 8 }} />
              </Option>
            )}
            {filteredAgents.map((agent) => (
              <Option key={agent.id} value={agent.id}>{agent.name}</Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <Button
            type="link"
            size="small"
            icon={showAdvanced ? <DownOutlined /> : <RightOutlined />}
            disabled={!selectedAgentId}
            onClick={() => selectedAgentId && setShowAdvanced(!showAdvanced)}
            style={{ padding: 0, fontSize: 13, color: selectedAgentId ? '#1890ff' : '#d9d9d9' }}
          >
            Advanced options
          </Button>
          {showAdvanced && selectedAgent && (
            <div style={{ marginTop: 10, padding: 16, background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', fontSize: 14 }}>
              {selectedAgent.aliases?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alias</label>
                  <Select
                    value={selectedAliasId || undefined}
                    onChange={handleAliasSelect}
                    loading={loadingAliasDetails}
                    disabled={hasSentOrResponded}
                    style={{ width: '100%' }}
                  >
                    {selectedAgent.aliases.map((alias: any) => (
                      <Option key={alias.name} value={alias.name}>{alias.name}</Option>
                    ))}
                  </Select>
                </div>
              )}
              <div style={{ marginBottom: loadingAliasDetails || aliasDetails?.agentInstructions ? 14 : 0 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Partner</label>
                <Select value={partner} onChange={setPartner} disabled={hasSentOrResponded} style={{ width: '100%' }}>
                  {PARTNER_OPTIONS.map((p) => (
                    <Option key={p.value} value={p.value}>{p.label}</Option>
                  ))}
                </Select>
              </div>
              {loadingAliasDetails && <Spin size="small" />}
              {!loadingAliasDetails && aliasDetails?.agentInstructions && (
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>System prompt</label>
                  <pre style={{
                    background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6,
                    padding: '10px 12px', fontSize: 12, color: '#595959', maxHeight: 180,
                    overflowY: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', margin: 0
                  }}>
                    {aliasDetails.agentInstructions}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Message
          </label>
          <div style={{ position: 'relative' }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!selectedAgentId || hasSentOrResponded}
              onFocus={(e) => { e.currentTarget.style.border = '1px solid #1890ff'; }}
              onBlur={(e) => { e.currentTarget.style.border = '1px solid #d9d9d9'; }}
              placeholder={selectedAgentId ? 'Enter your message…' : 'Select an agent first…'}
              style={{
                width: '100%', minHeight: 130, padding: '14px 16px', paddingBottom: 52,
                borderRadius: 8, border: '1px solid #d9d9d9', fontSize: 14, lineHeight: 1.6,
                resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                background: !selectedAgentId || hasSentOrResponded ? '#fafafa' : '#fff',
                color: !selectedAgentId || hasSentOrResponded ? '#8c8c8c' : '#262626',
                cursor: !selectedAgentId || hasSentOrResponded ? 'not-allowed' : 'text',
                transition: 'border-color 0.2s'
              }}
            />
            <div style={{ position: 'absolute', bottom: 10, right: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              {!hasSentOrResponded && (
                <span style={{ fontSize: 11, color: '#bfbfbf', userSelect: 'none' }}>
                  <kbd style={{ background: '#f0f0f0', border: '1px solid #d9d9d9', borderRadius: 3, padding: '1px 5px', fontSize: 10, color: '#8c8c8c' }}>⌘</kbd>
                  {' '}
                  <kbd style={{ background: '#f0f0f0', border: '1px solid #d9d9d9', borderRadius: 3, padding: '1px 5px', fontSize: 10, color: '#8c8c8c' }}>↵</kbd>
                </span>
              )}
              <Button
                icon={<SendOutlined />}
                disabled={isSendDisabled}
                onClick={handleSend}
                size="small"
              >
                Send
              </Button>
            </div>
          </div>
        </div>

        {(messages.length > 0 || isSending) && (
          <div style={{ opacity: isResetting ? 0 : 1, transition: 'opacity 0.2s ease' }}>

            {messages.map((msg, i) => (
              msg.role === 'user' ? null : (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Response
                    </span>
                  </div>
                  <div style={{
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderLeft: '3px solid #1890ff',
                    borderRadius: 8,
                    padding: '20px 24px',
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: '#262626'
                  }}>
                    <Markdown options={{
                      overrides: {
                        h1: { props: { style: { fontSize: 20, fontWeight: 700, marginBottom: 12, marginTop: 0, color: '#141414' } } },
                        h2: { props: { style: { fontSize: 16, fontWeight: 600, marginBottom: 10, marginTop: 20, color: '#262626', borderBottom: '1px solid #f0f0f0', paddingBottom: 6 } } },
                        h3: { props: { style: { fontSize: 14, fontWeight: 600, marginBottom: 8, marginTop: 16, color: '#434343' } } },
                        p:  { props: { style: { marginBottom: 12, marginTop: 0 } } },
                        ul: { props: { style: { paddingLeft: 20, marginBottom: 12 } } },
                        ol: { props: { style: { paddingLeft: 20, marginBottom: 12 } } },
                        li: { props: { style: { marginBottom: 5 } } },
                        a:  { props: { style: { color: '#1890ff' }, target: '_blank', rel: 'noreferrer' } },
                        strong: { props: { style: { fontWeight: 600, color: '#141414' } } },
                        code: { props: { style: { background: '#f5f5f5', borderRadius: 4, padding: '1px 5px', fontSize: 13, fontFamily: 'monospace' } } },
                        pre: { props: { style: { background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', overflowX: 'auto', fontSize: 13 } } },
                      }
                    }}>
                      {msg.content}
                    </Markdown>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <Button
                      size="small"
                      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={copyResponse}
                      type={copied ? 'primary' : 'default'}
                      ghost={copied}
                      style={copied ? { color: '#52c41a', borderColor: '#52c41a' } : undefined}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={downloadResponse}
                    >
                      Download
                    </Button>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={resetSession}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              )
            ))}

            {isSending && (
              <div style={{
                display: 'flex', gap: 10, alignItems: 'center',
                padding: '14px 16px', background: '#fff', borderRadius: 8,
                border: '1px solid #f0f0f0', borderLeft: '3px solid #1890ff',
                color: '#8c8c8c', fontSize: 13, marginBottom: 16
              }}>
                <Spin size="small" />
                <span>Agent is thinking…</span>
              </div>
            )}

            {error && (
              <Alert
                type="error"
                message={error}
                style={{ marginBottom: 16 }}
                action={
                  !lastAgentMessage ? (
                    <Button size="small" icon={<ReloadOutlined />} onClick={resetSession}>
                      Reset
                    </Button>
                  ) : undefined
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AgentInteractionV2: React.FC = () => <AgentInteractionV2Inner />;

export { AgentInteractionV2 };
export default AgentInteractionV2;
